import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API, formatApiError, mediaUrl } from "@/lib/api";
import { youtubeEmbedUrl } from "@/lib/youtube";
import { themes } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import RichText from "@/components/RichText";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Heart, MapPin, Clock, BedDouble, Shirt, Phone, Mail, Car, Bookmark, MessageCircle, Send,
  Loader2, PartyPopper, HeartCrack, CheckCircle2, ExternalLink, Quote,
} from "lucide-react";
import { toast } from "sonner";

function Section({ id, children, className = "", style }) {
  return (
    <section
      id={id}
      style={style}
      className={`relative mx-auto w-full max-w-5xl scroll-mt-24 px-6 py-20 md:py-28 ${className}`}
    >
      {children}
    </section>
  );
}

function Overline({ children }) {
  return <p className="overline mb-4 text-[#1A362D]/70">{children}</p>;
}

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** Format YYYY-MM-DD (or ISO) as DD-MON-YYYY, e.g. 14-DEC-2026 */
function formatDisplayDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  const day = m[3];
  const mon = MONTHS[Number(m[2]) - 1] || m[2];
  return `${day}-${mon}-${m[1]}`;
}

function formatDateRange(start, end) {
  const a = formatDisplayDate(start);
  const b = formatDisplayDate(end);
  if (a && b && a !== b) return `${a} – ${b}`;
  return a || b || "";
}

function bookmarkShortcutHint() {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "In Safari, tap Share, then Add Bookmark.";
  const isMac = /Mac/.test(navigator.platform || "") || ua.includes("Mac OS");
  return isMac ? "Press Command+D to bookmark this page." : "Press Ctrl+D to bookmark this page.";
}

async function saveInviteBookmark(title) {
  const url = window.location.href;
  try {
    if (typeof window.external?.AddFavorite === "function") {
      window.external.AddFavorite(url, title);
      toast.success("Invitation bookmarked.");
      return;
    }
  } catch {
    /* browsers no longer allow adding bookmarks directly */
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success(`Invite link copied. ${bookmarkShortcutHint()}`);
  } catch {
    toast.message(`Save this invite link to check back later: ${url}`);
  }
}

export default function GuestInvite() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [errorTitle, setErrorTitle] = useState("Invitation Not Found");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/invite/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 403) {
          setErrorTitle("Something went wrong.");
          setError("Please let the host know.");
        } else {
          setErrorTitle("Invitation Not Found");
          setError("This invitation could not be found. Please check your link.");
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading)
    return (
      <div className="flex h-screen items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
      </div>
    );

  if (error || !data)
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-[#FDFBF7] px-6 text-center" data-testid="invite-error">
        <Heart className="mb-4 h-8 w-8 text-[#1A362D]" />
        <h1 className="font-serif text-3xl text-[#1C1917]">{errorTitle}</h1>
        <p className="mt-3 max-w-sm text-[#57534E]">{error}</p>
      </div>
    );

  const {
    guest, event, schedule, gallery, deadline_passed, wishes,
    assigned_rooms = [], assigned_rides = [], rsvp_deadline, rsvp_deadline_personal,
  } = data;
  const t = themes[event.theme] || themes.emerald;
  const accent = event.theme_primary || t.accent;
  const activities = (event.activity_options || []).filter((a) => a.active !== false);
  const hotelContacts = event.hotel_contacts || [];

  const scheduleGroups = [];
  const seenDays = {};
  schedule.forEach((s) => {
    const k = s.day || "Schedule";
    if (!seenDays[k]) { seenDays[k] = []; scheduleGroups.push([k, seenDays[k]]); }
    seenDays[k].push(s);
  });

  return (
    <div className="relative min-h-screen text-[#1C1917]" style={{ background: t.bg }}>
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/40 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3">
          <a href="#top" className="font-serif text-lg font-semibold" style={{ color: accent }}>
            {(event.partner_one || "").split(" ")[0]} &amp; {(event.partner_two || "").split(" ")[0]}
          </a>
          <div className="hidden items-center gap-6 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1C1917] md:flex">
            <a href="#story" className="transition-opacity hover:opacity-50">Story</a>
            {schedule.length > 0 && <a href="#schedule" className="transition-opacity hover:opacity-50">Schedule</a>}
            <a href="#venue" className="transition-opacity hover:opacity-50">Venue</a>
            {(event.accommodation || event.dress_code || assigned_rooms.length > 0 || hotelContacts.length > 0) && (
              <a href="#stay" className="transition-opacity hover:opacity-50">Stay</a>
            )}
            {(activities.length > 0 || event.explore_intro) && (
              <a href="#explore" className="transition-opacity hover:opacity-50">Explore</a>
            )}
            {gallery.length > 0 && <a href="#gallery" className="transition-opacity hover:opacity-50">Gallery</a>}
            {event.photos_url && <a href="#photos" className="transition-opacity hover:opacity-50">Photos</a>}
            {event.faqs?.length > 0 && <a href="#faq" className="transition-opacity hover:opacity-50">FAQ</a>}
          </div>
          <a href="#rsvp" data-testid="nav-rsvp" className="rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-white transition-transform hover:-translate-y-0.5" style={{ background: accent }}>RSVP</a>
        </div>
      </nav>
      {/* HERO */}
      <section id="top" className="relative flex min-h-screen items-center justify-center overflow-hidden">
        {event.hero_image && (
          <img
            src={event.hero_image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0" style={{ background: t.overlay || "linear-gradient(to bottom, rgba(0,0,0,.45), rgba(0,0,0,.65))" }} />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1 }}
          className="relative z-10 mx-auto max-w-3xl px-6 text-center text-white"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/75 md:text-xs">
            You are cordially invited to
          </p>

          {event.name && (
            <h1 className="mt-5 font-serif text-4xl font-light leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
              {event.name}
            </h1>
          )}

          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:mt-6 md:text-5xl">
            {event.partner_one}
            <span className="mx-2 italic text-white/65 md:mx-3">&amp;</span>
            {event.partner_two}
          </h2>

          {event.subtitle && (
            <p className="mx-auto mt-6 max-w-xl font-serif text-lg italic leading-relaxed text-white/90 md:text-xl">
              {event.subtitle}
            </p>
          )}

          <div className="mx-auto my-8 h-px w-20 bg-white/35" />

          {(event.start_date || event.venue) && (
            <p className="text-sm tracking-[0.12em] text-white/85 md:text-base">
              {formatDateRange(event.start_date, event.end_date)}
              {event.start_date && event.venue ? <span className="mx-2 text-white/40">·</span> : null}
              {event.venue}
            </p>
          )}

          <p className="mt-8 font-serif text-xl text-white/95 md:text-2xl">
            Dear {guest.primary_guest_name},
          </p>

          <a
            href="#rsvp"
            data-testid="hero-rsvp-cta"
            className="mt-10 inline-block rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-[#1A362D] transition-transform hover:-translate-y-0.5"
          >
            RSVP Now
          </a>
        </motion.div>
      </section>

      {/* WELCOME / STORY */}
      <Section id="story" className="grain">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <div>
            <Overline>Our Story</Overline>
            <h2 className="font-serif text-4xl font-light leading-tight md:text-5xl">{event.welcome_message ? "With love & gratitude" : "Our Journey"}</h2>
            <RichText html={event.welcome_message} className="mt-6 text-lg leading-relaxed" />
            <RichText html={event.couple_story} className="mt-4 leading-relaxed" />
          </div>
          {event.couple_image && (
            <div className="overflow-hidden rounded-[2rem] shadow-xl">
              <img src={event.couple_image} alt="Couple" className="h-[520px] w-full object-cover" />
            </div>
          )}
        </div>
      </Section>

      {/* SCHEDULE */}
      {schedule.length > 0 && (
        <Section id="schedule" style={{ background: t.secondary }}>
          <div className="mb-14 text-center">
            <Overline>The Celebrations</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">Order of Events</h2>
          </div>
          <div className="mx-auto max-w-3xl space-y-16">
            {scheduleGroups.map(([day, items]) => (
              <div key={day}>
                <div className="mb-8 flex items-center gap-4">
                  <span className="h-px flex-1 bg-[#1C1917]/15" />
                  <h3 className="text-center font-serif text-2xl md:text-3xl" style={{ color: accent }}>{day}</h3>
                  <span className="h-px flex-1 bg-[#1C1917]/15" />
                </div>
                <div className="space-y-4">
                  {items.map((s, i) => (
                    <motion.div
                      key={s.id}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex gap-5 rounded-2xl border border-[#1C1917]/10 bg-white/60 p-5 backdrop-blur-sm"
                    >
                      <div className="w-20 shrink-0 pt-0.5 text-right">
                        <div className="text-sm font-semibold" style={{ color: accent }}>{s.start_time || "—"}</div>
                        {s.end_time && <div className="text-[11px] text-[#57534E]">to {s.end_time}</div>}
                      </div>
                      <div className="w-px shrink-0" style={{ background: `${accent}33` }} />
                      <div className="flex-1">
                        <h4 className="font-serif text-xl">{s.name}</h4>
                        {s.description && <RichText html={s.description} className="mt-1 text-sm leading-relaxed" />}
                        {s.venue && <p className="mt-2 inline-flex items-center gap-1 text-xs text-[#57534E]"><MapPin className="h-3 w-3" /> {s.venue}</p>}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* VENUE */}
      <Section id="venue">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <Overline>The Venue</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">{event.venue}</h2>
            <p className="mt-4 flex items-start gap-2 text-lg text-[#57534E]"><MapPin className="mt-1 h-5 w-5 shrink-0" style={{ color: accent }} /> {event.venue_address}</p>
            {event.google_maps_link && (
              <a
                href={event.google_maps_link}
                target="_blank"
                rel="noreferrer"
                data-testid="maps-link"
                className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                style={{ background: accent }}
              >
                Open in Google Maps <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
          {event.google_maps_link && (
            <div className="overflow-hidden rounded-[2rem] border border-[#1C1917]/10 shadow-lg">
              <iframe
                title="map"
                width="100%"
                height="360"
                loading="lazy"
                style={{ border: 0 }}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(event.venue_address || event.venue)}&output=embed`}
              />
            </div>
          )}
        </div>
      </Section>

      {/* TRANSFERS, THEN STAY */}
      {(assigned_rides.length > 0) && (
        <Section id="transfers">
          <div className="mb-10 text-center">
            <Overline>Getting There</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">Your transfers</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {assigned_rides.map((ride) => (
              <div key={ride.id} className="rounded-2xl border border-[#1C1917]/10 bg-white/80 p-6" data-testid="assigned-ride-card">
                <p className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: ride.vehicle?.color || accent }}>
                  {ride.direction === "departure" ? "Drop-off" : "Pickup"}
                </p>
                <h3 className="mt-2 font-serif text-2xl">{ride.vehicle?.name || "Vehicle"}</h3>
                <p className="mt-1 text-sm text-[#57534E]">
                  {[ride.vehicle?.vehicle_number, ride.date, ride.time, ride.location].filter(Boolean).join(" · ")}
                </p>
                {(ride.vehicle?.driver_name || ride.vehicle?.driver_phone) && (
                  <p className="mt-2 text-sm text-[#57534E]">
                    {ride.vehicle.driver_name}{ride.vehicle.driver_phone ? ` · ${ride.vehicle.driver_phone}` : ""}
                  </p>
                )}
                <p className="mt-2 text-xs text-[#78716C]">{ride.party_size} seat{ride.party_size === 1 ? "" : "s"} reserved for your family</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(event.accommodation || event.dress_code || assigned_rooms.length > 0 || hotelContacts.length > 0 || event.hotel_name) && (
        <Section id="stay" className="text-[#FDFBF7]" style={{ background: t.band }}>
          {assigned_rooms.length > 0 && (
            <div className="mb-14">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">Your stay</p>
              <h2 className="font-serif text-4xl font-light">Your accommodation</h2>
              {(event.hotel_name || event.hotel_address) && (
                <p className="mt-2 text-white/75">
                  {event.hotel_name}{event.hotel_address ? ` · ${event.hotel_address}` : ""}
                </p>
              )}
              <div className="mt-8 grid gap-8">
                {assigned_rooms.map((room) => (
                  <div key={room.id} className="rounded-2xl border border-white/20 bg-white/10 p-6 backdrop-blur" data-testid="assigned-room-card">
                    <h3 className="font-serif text-2xl">Room {room.room_number}</h3>
                    <p className="mt-1 text-sm text-white/70">
                      {[room.category, room.bed_type, room.view, room.level].filter(Boolean).join(" · ")}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {room.accessible && <span className="rounded-full border border-white/30 px-3 py-1">Accessible</span>}
                      {room.lift_connected && <span className="rounded-full border border-white/30 px-3 py-1">Lift connected</span>}
                      {room.connecting && (
                        <span className="rounded-full border border-white/30 px-3 py-1">
                          Connecting{room.connecting_room_number ? ` · ${room.connecting_room_number}` : ""}
                        </span>
                      )}
                      {room.extra_beds > 0 && (
                        <span className="rounded-full border border-white/30 px-3 py-1" data-testid="assigned-extra-bed">
                          Extra bed ×{room.extra_beds}
                        </span>
                      )}
                    </div>
                    {room.assignment_notes && <p className="mt-3 text-sm text-white/80">{room.assignment_notes}</p>}
                    {(room.media || []).length > 0 && (
                      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                        {room.media.map((m) => (
                          m.media_type === "video" ? (
                            <video key={m.id} src={mediaUrl(m.url)} controls className="h-36 w-full rounded-lg object-cover" />
                          ) : (
                            <img key={m.id} src={mediaUrl(m.url)} alt="" className="h-36 w-full rounded-lg object-cover" />
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hotelContacts.length > 0 && (
            <div className="mb-14">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">Need anything?</p>
              <h3 className="font-serif text-3xl font-light">Hotel contacts</h3>
              <p className="mt-2 max-w-lg text-sm text-white/70">
                Reach reception or concierge directly for personal needs during your stay.
              </p>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {hotelContacts.map((c) => (
                  <div key={c.id || `${c.role}-${c.phone}`} className="rounded-xl border border-white/20 bg-white/10 p-4">
                    <p className="text-xs uppercase tracking-wider text-white/60">{c.role}</p>
                    {c.name && <p className="mt-1 font-medium">{c.name}</p>}
                    <div className="mt-2 space-y-1 text-sm">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:underline">
                          <Phone className="h-3.5 w-3.5" /> {c.phone}
                        </a>
                      )}
                      {c.email && (
                        <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:underline">
                          <Mail className="h-3.5 w-3.5" /> {c.email}
                        </a>
                      )}
                      {c.notes && <p className="text-white/65">{c.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-12 md:grid-cols-2">
            {event.accommodation && (
              <div>
                <BedDouble className="mb-4 h-7 w-7 text-white/80" />
                <h3 className="font-serif text-3xl font-light">Accommodation</h3>
                <RichText html={event.accommodation} className="mt-4 leading-relaxed" light />
              </div>
            )}
            {event.dress_code && (
              <div>
                <Shirt className="mb-4 h-7 w-7 text-white/80" />
                <h3 className="font-serif text-3xl font-light">Dress Code</h3>
                <RichText html={event.dress_code} className="mt-4 leading-relaxed" light />
              </div>
            )}
          </div>
        </Section>
      )}

      {/* EXPLORE CATALOG */}
      {(activities.length > 0 || event.explore_intro) && (
        <Section id="explore">
          <div className="mb-10 text-center">
            <Overline>While you are here</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">Explore</h2>
            {event.explore_intro && (
              <RichText html={event.explore_intro} className="mx-auto mt-4 max-w-2xl text-[#57534E]" />
            )}
            <p className="mx-auto mt-3 max-w-md text-sm text-[#78716C]" data-testid="explore-concierge-note">
              Mark interest on the RSVP — nothing is booked here. We will connect you with our concierge to arrange it.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {activities.map((a) => (
              <div key={a.id} className="rounded-2xl border border-[#1C1917]/10 bg-white/70 p-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#78716C]">{a.category}</p>
                <h3 className="mt-2 font-serif text-2xl">{a.name}</h3>
                {a.timing_hint && <p className="mt-1 text-xs text-[#57534E]">{a.timing_hint}</p>}
                {a.description && <p className="mt-3 text-sm leading-relaxed text-[#57534E]">{a.description}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* GALLERY */}
      {gallery.length > 0 && <InviteGallery gallery={gallery} />}

      {/* FAQ */}
      {event.faqs?.length > 0 && (
        <Section id="faq" style={{ background: t.secondary }}>
          <div className="mb-10 text-center">
            <Overline>Good to Know</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">Questions & Answers</h2>
          </div>
          <Accordion type="single" collapsible className="mx-auto max-w-2xl">
            {event.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-[#1C1917]/10">
                <AccordionTrigger className="font-serif text-xl hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-[#57534E]">
                  <RichText html={f.answer} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      )}

      {event.photos_url && (
        <Section id="photos">
          <div className="mx-auto max-w-xl text-center">
            <Overline>After the celebration</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">{event.photos_label || "Photographs"}</h2>
            {event.photos_note && <p className="mt-4 text-[#57534E]">{event.photos_note}</p>}
            <a
              href={event.photos_url}
              target="_blank"
              rel="noreferrer"
              data-testid="photos-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white"
              style={{ background: accent }}
            >
              Open photographs <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </Section>
      )}

      {/* RSVP */}
      <RSVPSection
        token={token}
        guest={guest}
        accent={accent}
        deadlinePassed={deadline_passed}
        deadline={formatDisplayDate(rsvp_deadline || event.rsvp_deadline)}
        deadlinePersonal={!!rsvp_deadline_personal}
        activities={activities}
        detailsEditable={!!data.details_editable}
        wishesEditable={!!data.wishes_editable}
        attendanceLocked={!!data.attendance_locked}
        detailsUntil={formatDisplayDate(data.details_until)}
        wishesUntil={formatDisplayDate(data.wishes_until)}
      />

      {/* WISHES / GUEST BOOK */}
      {wishes?.length > 0 && (
        <Section id="wishes" style={{ background: t.secondary }}>
          <div className="mb-12 text-center">
            <Overline>From the Heart</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl">Well Wishes</h2>
          </div>
          <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
            {wishes.map((w, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="mb-6 break-inside-avoid rounded-2xl border border-[#1C1917]/10 bg-white/70 p-6 backdrop-blur-sm"
                data-testid={`wish-card-${i}`}
              >
                <Quote className="mb-3 h-5 w-5" style={{ color: accent }} />
                <RichText html={w.comment} className="font-serif text-lg italic leading-relaxed text-[#1C1917]" />
                <p className="mt-3 text-sm font-semibold" style={{ color: accent }}>— {w.name}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* CONTACT */}
      {event.contacts?.length > 0 && (
        <Section id="contact" className="text-[#FDFBF7]" style={{ background: t.band }}>
          <div className="mb-10 text-center">
            <Overline>Reach Out</Overline>
            <h2 className="font-serif text-4xl font-light md:text-5xl text-white">Contact</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {event.contacts.map((c, i) => (
              <div key={i} className="rounded-2xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm">
                <div className="font-serif text-2xl">{c.name}</div>
                <div className="text-sm text-white/60">{c.role}</div>
                <div className="mt-3 space-y-1 text-sm text-white/80">
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2"><Phone className="h-4 w-4" /> {c.phone}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2"><Mail className="h-4 w-4" /> {c.email}</a>}
                </div>
              </div>
            ))}
          </div>
          {event.emergency_contact && (
            <p className="mt-8 text-center text-sm text-white/60">Emergency contact: {event.emergency_contact}</p>
          )}
        </Section>
      )}

      <div id="save-invite" className="mx-auto max-w-lg px-6 pb-4 pt-16 text-center">
        <Bookmark className="mx-auto mb-4 h-6 w-6" style={{ color: accent }} />
        <p className="font-serif text-2xl md:text-3xl">Save this invitation</p>
        <p className="mt-3 text-sm leading-relaxed text-[#57534E]" data-testid="bookmark-invite-note">
          Please check back as we get closer to the event — we will add more details here. Bookmark this page so you always have your invite link.
        </p>
        <Button
          type="button"
          className="mt-6 px-6 py-5 text-sm"
          style={{ background: accent }}
          onClick={() => saveInviteBookmark(event.name || `${event.partner_one || ""} & ${event.partner_two || ""}`.trim())}
          data-testid="bookmark-invite-button"
        >
          <Bookmark className="mr-2 h-4 w-4" /> Bookmark invite link
        </Button>
      </div>

      <footer className="py-12 text-center" style={{ background: t.bg }}>
        <Heart className="mx-auto mb-3 h-5 w-5" style={{ color: accent }} fill={accent} />
        <p className="font-serif text-2xl">{event.partner_one} &amp; {event.partner_two}</p>
        <p className="mt-1 text-xs tracking-wide text-[#57534E]">{formatDateRange(event.start_date, event.end_date)}</p>
      </footer>
      <HostChat token={token} accent={accent} guestName={guest.primary_guest_name} />
    </div>
  );
}

function HostChat({ token, accent, guestName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scroller = useRef(null);

  const load = async (markRead) => {
    const { data } = await axios.get(`${API}/invite/${token}/messages`, { params: { mark_read: markRead } });
    setMessages(data.messages || []);
    setUnread(data.unread || 0);
  };

  useEffect(() => {
    load(false).catch(() => {});
    const timer = setInterval(() => load(open).catch(() => {}), 8000);
    return () => clearInterval(timer);
  }, [token, open]);

  useEffect(() => {
    if (open && scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      await axios.post(`${API}/invite/${token}/messages`, { body });
      setDraft("");
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end" data-testid="guest-chat">
      {open && (
        <div className="mb-3 flex h-[28rem] w-[min(22rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-[#1C1917]/10 bg-white shadow-2xl">
          <div className="px-4 py-3 text-white" style={{ background: accent }}>
            <p className="text-sm font-semibold">Message the hosts</p>
            <p className="text-xs text-white/80">This conversation stays on your invitation.</p>
          </div>
          <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto bg-[#FDFBF7] p-3">
            {messages.length === 0 && (
              <p className="px-2 py-6 text-center text-sm text-[#78716C]">Ask us anything. We will reply here.</p>
            )}
            {messages.map((m) => {
              const mine = m.sender === "guest";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? "text-white" : "bg-white text-[#1C1917]"}`} style={mine ? { background: accent } : undefined}>
                    <p className="text-[10px] uppercase tracking-wide opacity-70">{m.notice ? "Notice" : mine ? guestName : m.sender_name || "Host"}</p>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 border-t border-[#E5E5E5] p-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write a message"
              className="min-h-[44px]"
              data-testid="guest-chat-input"
            />
            <Button type="button" onClick={send} disabled={sending} className="self-end" style={{ background: accent }} data-testid="guest-chat-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) load(true).catch(() => {});
        }}
        className="relative ml-auto flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg"
        style={{ background: accent }}
        data-testid="guest-chat-toggle"
        aria-label="Message the hosts"
      >
        <MessageCircle className="h-6 w-6" />
        {unread > 0 && !open && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B91C1C] px-1 text-[10px]">{unread}</span>
        )}
      </button>
    </div>
  );
}

function groupGallerySections(items) {
  const map = new Map();
  for (const item of items) {
    const key = (item.album || "Moments").trim() || "Moments";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }
  return [...map.entries()].sort((a, b) => {
    const amin = Math.min(...a[1].map((i) => i.display_order ?? 0));
    const bmin = Math.min(...b[1].map((i) => i.display_order ?? 0));
    return amin - bmin || a[0].localeCompare(b[0]);
  });
}

function GalleryMediaGrid({ items }) {
  const spans = ["md:col-span-8 md:row-span-2", "md:col-span-4", "md:col-span-4", "md:col-span-6", "md:col-span-6"];
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-12">
      {items.map((g, i) => {
        const kind = g.media_type || "image";
        const caption = (g.caption || "").trim();
        const ytEmbed = kind === "youtube"
          ? youtubeEmbedUrl(g.youtube_id || g.youtube_url || g.video_url)
          : "";
        return (
          <motion.div
            key={g.id}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: Math.min(i * 0.05, 0.3) }}
            className={`flex min-h-[200px] flex-col overflow-hidden rounded-2xl bg-black/5 ${spans[i % spans.length]}`}
          >
            <div className="relative min-h-[180px] flex-1 overflow-hidden">
              {kind === "youtube" && ytEmbed ? (
                <iframe
                  title={caption || "YouTube video"}
                  src={ytEmbed}
                  className="absolute inset-0 h-full w-full bg-black"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  data-testid={`gallery-youtube-${g.id}`}
                />
              ) : kind === "video" ? (
                <video
                  src={mediaUrl(g.video_url)}
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full min-h-[180px] w-full bg-black object-cover"
                  data-testid={`gallery-video-${g.id}`}
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <img
                  src={g.image_data}
                  alt={caption || "Gallery"}
                  className="h-full min-h-[180px] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              )}
            </div>
            {caption ? (
              <p
                className="shrink-0 border-t border-[#1C1917]/5 bg-white/90 px-3 py-2.5 text-sm leading-snug text-[#44403C]"
                data-testid={`gallery-caption-${g.id}`}
              >
                {caption}
              </p>
            ) : null}
          </motion.div>
        );
      })}
    </div>
  );
}

function InviteGallery({ gallery }) {
  const sections = useMemo(() => groupGallerySections(gallery), [gallery]);
  const [active, setActive] = useState(sections[0]?.[0] || "Moments");
  const showTabs = sections.length > 1;
  const activeItems = (sections.find(([name]) => name === active) || sections[0] || ["Moments", []])[1];

  useEffect(() => {
    if (!sections.some(([name]) => name === active) && sections[0]) {
      setActive(sections[0][0]);
    }
  }, [sections, active]);

  return (
    <Section id="gallery">
      <div className="mb-10 text-center">
        <Overline>Moments</Overline>
        <h2 className="font-serif text-4xl font-light md:text-5xl">Gallery</h2>
      </div>
      {showTabs && (
        <div
          className="mb-8 flex flex-wrap items-center justify-center gap-2"
          role="tablist"
          aria-label="Gallery sections"
          data-testid="gallery-section-tabs"
        >
          {sections.map(([name]) => {
            const selected = name === active;
            return (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActive(name)}
                data-testid={`gallery-tab-${name}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] transition-all ${
                  selected
                    ? "bg-[#1A362D] text-white shadow-sm"
                    : "border border-[#1C1917]/15 bg-white/70 text-[#57534E] hover:border-[#1A362D]/40"
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}
      <GalleryMediaGrid items={activeItems} />
    </Section>
  );
}

/** Guests aged this many years or younger are counted as children. */
const CHILD_MAX_AGE_YEARS = 6;

function RSVPSection({
  token, guest, accent, deadlinePassed, deadline, deadlinePersonal, activities = [],
  detailsEditable = true, wishesEditable = true, attendanceLocked = false, detailsUntil = "", wishesUntil = "",
}) {
  const existing = guest.rsvp || {};
  const existingTravel = existing.travel || {};
  const submitted = existing.status === "attending" || existing.status === "declined";
  const [status, setStatus] = useState(existing.status && existing.status !== "pending" ? existing.status : "");
  const [adults, setAdults] = useState(
    existing.adults_attending != null && existing.adults_attending !== ""
      ? String(existing.adults_attending)
      : "1",
  );
  const [children, setChildren] = useState(
    existing.children_attending != null && existing.children_attending !== ""
      ? String(existing.children_attending)
      : "0",
  );
  const [accessible, setAccessible] = useState(!!existing.accessible_room_required);
  const [transport, setTransport] = useState(!!existing.transportation_required);
  const [meal, setMeal] = useState(existing.meal_preference || "");
  const [activityIds, setActivityIds] = useState(existing.activity_request_ids || []);
  const [activityNotes, setActivityNotes] = useState(existing.activity_notes || "");
  const [comments, setComments] = useState(existing.comments || "");
  const [specialRequest, setSpecialRequest] = useState(existing.special_request || "");
  const [extraBed, setExtraBed] = useState(!!existing.extra_bed_required);
  const [extraBeds, setExtraBeds] = useState(String(existing.extra_beds || 1));
  const [arrivalMode, setArrivalMode] = useState(existingTravel.arrival_mode || "");
  const [arrivalDetails, setArrivalDetails] = useState(existingTravel.arrival_details || "");
  const [arrivalDate, setArrivalDate] = useState(existingTravel.arrival_date || "");
  const [arrivalTime, setArrivalTime] = useState(existingTravel.arrival_time || "");
  const [departureDate, setDepartureDate] = useState(existingTravel.departure_date || "");
  const [departureTime, setDepartureTime] = useState(existingTravel.departure_time || "");
  const [dropoff, setDropoff] = useState(existingTravel.dropoff_location || "");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const toggleActivity = (id) => {
    setActivityIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (!status) return toast.error("Please let us know if you can attend");
    setSaving(true);
    try {
      await axios.post(`${API}/invite/${token}/rsvp`, {
        status,
        adults_attending: status === "attending" ? Number(adults) || 0 : 0,
        children_attending: status === "attending" ? Number(children) || 0 : 0,
        accommodation_required: false,
        stay_check_in: null,
        stay_check_out: null,
        accessible_room_required: status === "attending" ? accessible : false,
        extra_bed_required: status === "attending" ? extraBed : false,
        extra_beds: status === "attending" && extraBed ? Number(extraBeds) || 1 : 0,
        transportation_required: status === "attending" ? transport || !!arrivalMode : false,
        meal_preference: status === "attending" ? meal : "",
        activity_request_ids: status === "attending" ? activityIds : [],
        activity_notes: status === "attending" ? activityNotes : "",
        comments,
        special_request: specialRequest,
        travel: status === "attending" ? {
          arrival_mode: arrivalMode,
          arrival_details: arrivalDetails,
          arrival_date: arrivalDate,
          arrival_time: arrivalTime,
          departure_date: departureDate,
          departure_time: departureTime,
          dropoff_location: dropoff,
          notes: "",
        } : {},
      });
      setDone(true);
      toast.success(
        status === "attending" && activityIds.length > 0
          ? "Thank you! We'll connect you with our concierge to book the tours you selected."
          : "Thank you! Your RSVP has been recorded.",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit RSVP");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section id="rsvp" className="grain">
      <div className="mx-auto max-w-xl text-center">
        <Overline>Will You Join Us?</Overline>
        <h2 className="font-serif text-4xl font-light md:text-5xl">RSVP</h2>
        {deadline && (
          <p className="mt-3 text-sm text-[#57534E]" data-testid="rsvp-deadline-note">
            {deadlinePersonal ? `Your response date has been extended. Kindly respond by ${deadline}` : `Kindly respond by ${deadline}`}
          </p>
        )}
        <p className="mx-auto mt-3 max-w-md text-sm text-[#57534E]" data-testid="rsvp-child-age-note">
          Guests aged {CHILD_MAX_AGE_YEARS} years and under are counted as children.
        </p>
      </div>

      {done ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mx-auto mt-10 max-w-md rounded-2xl border border-[#1A362D]/15 bg-white/70 p-10 text-center backdrop-blur-xl" data-testid="rsvp-success">
          <CheckCircle2 className="mx-auto mb-4 h-10 w-10" style={{ color: accent }} />
          <h3 className="font-serif text-2xl">
            {status === "attending" ? "We can't wait to celebrate with you!" : "We'll miss you dearly."}
          </h3>
          <p className="mt-2 text-sm text-[#57534E]">
            {attendanceLocked
              ? "Your update has been saved."
              : "Your response has been recorded. You may update it anytime before the deadline."}
          </p>
          {status === "attending" && activityIds.length > 0 && (
            <p className="mt-3 text-sm text-[#57534E]" data-testid="rsvp-activity-followup">
              We will connect you with our concierge to book the tours and activities you selected.
            </p>
          )}
          <Button variant="outline" className="mt-6" onClick={() => setDone(false)} data-testid="edit-rsvp-button">Update Response</Button>
        </motion.div>
      ) : deadlinePassed && !detailsEditable && !wishesEditable ? (
        <div className="mx-auto mt-10 max-w-md rounded-2xl border border-[#1A362D]/15 bg-white/70 p-10 text-center backdrop-blur-xl">
          <p className="text-[#57534E]">The RSVP deadline has passed. Please contact the hosts directly.</p>
        </div>
      ) : (
        <div className="mx-auto mt-10 max-w-xl rounded-3xl border border-[#1C1917]/10 bg-white/70 p-8 shadow-xl backdrop-blur-xl md:p-10">
          {attendanceLocked && (
            <p className="mb-6 text-center text-sm text-[#57534E]" data-testid="rsvp-late-note">
              Your acceptance is confirmed.
              {detailsEditable && detailsUntil ? ` Stay, travel, meals, tours, and requests can be updated through ${detailsUntil}.` : ""}
              {wishesEditable && wishesUntil ? ` Your note to the couple can be updated through ${wishesUntil}.` : ""}
            </p>
          )}
          {submitted && !attendanceLocked && <p className="mb-6 text-center text-xs text-[#57534E]">You responded earlier — feel free to update below.</p>}
          {!attendanceLocked && (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setStatus("attending")}
              data-testid="rsvp-attending"
              className={`flex flex-col items-center gap-2 rounded-2xl border-2 py-6 transition-all ${status === "attending" ? "border-[#1A362D] bg-[#1A362D] text-white" : "border-[#1C1917]/15 hover:border-[#1A362D]"}`}
            >
              <PartyPopper className="h-6 w-6" />
              <span className="text-sm font-semibold">Joyfully Accept</span>
            </button>
            <button
              onClick={() => setStatus("declined")}
              data-testid="rsvp-declined"
              className={`flex flex-col items-center gap-2 rounded-2xl border-2 py-6 transition-all ${status === "declined" ? "border-[#B91C1C] bg-[#B91C1C] text-white" : "border-[#1C1917]/15 hover:border-[#B91C1C]"}`}
            >
              <HeartCrack className="h-6 w-6" />
              <span className="text-sm font-semibold">Regretfully Decline</span>
            </button>
          </div>
          )}

          {status === "attending" && (
            <div className="mt-6 grid grid-cols-2 gap-4" data-testid="rsvp-headcount">
              <div className="space-y-2">
                <Label htmlFor="rsvp-adults">Adults Attending</Label>
                <Input
                  id="rsvp-adults"
                  type="number"
                  min={0}
                  value={adults}
                  readOnly={attendanceLocked}
                  onChange={(e) => {
                    if (attendanceLocked) return;
                    const next = Math.max(0, Number(e.target.value) || 0);
                    setAdults(String(next));
                  }}
                  data-testid="rsvp-adults"
                  className={attendanceLocked ? "bg-[#F5F5F4]" : ""}
                />
                <p className="text-xs text-[#78716C]">
                  {attendanceLocked ? "Confirmed" : `Age ${CHILD_MAX_AGE_YEARS + 1}+`}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rsvp-children">Children Attending</Label>
                <Input
                  id="rsvp-children"
                  type="number"
                  min={0}
                  value={children}
                  readOnly={attendanceLocked}
                  onChange={(e) => {
                    if (attendanceLocked) return;
                    const next = Math.max(0, Number(e.target.value) || 0);
                    setChildren(String(next));
                  }}
                  data-testid="rsvp-children"
                  className={attendanceLocked ? "bg-[#F5F5F4]" : ""}
                />
                <p className="text-xs text-[#78716C]">
                  {attendanceLocked ? "Confirmed" : `Age ${CHILD_MAX_AGE_YEARS} and under`}
                </p>
              </div>
            </div>
          )}

          {status === "attending" && detailsEditable && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 space-y-5"
              data-testid="rsvp-attending-details"
            >
              <div className="space-y-3 rounded-xl border border-[#1C1917]/10 bg-white/80 px-4 py-3">
                <div className="flex items-start gap-2 text-sm leading-relaxed text-[#44403C]">
                  <BedDouble className="mt-0.5 h-4 w-4 shrink-0" />
                  <p data-testid="rsvp-stay-note">
                    Accommodation for the event days is already booked for you. Our team will share your room details separately.
                  </p>
                </div>
                <div className="flex items-center justify-between border-t border-[#1C1917]/10 pt-3">
                  <span className="text-sm">Extra bed required</span>
                  <Switch
                    checked={extraBed}
                    onCheckedChange={setExtraBed}
                    data-testid="rsvp-extra-bed"
                    className="data-[state=checked]:bg-[#1A362D] data-[state=unchecked]:bg-[#D6D3D1]"
                  />
                </div>
                {extraBed && (
                  <div className="space-y-2">
                    <Label htmlFor="rsvp-extra-beds">How many extra beds?</Label>
                    <Input
                      id="rsvp-extra-beds"
                      type="number"
                      min={1}
                      max={4}
                      value={extraBeds}
                      onChange={(e) => setExtraBeds(e.target.value)}
                      data-testid="rsvp-extra-beds"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-[#1C1917]/10 pt-3">
                  <span className="text-sm">Accessible room required</span>
                  <Switch
                    checked={accessible}
                    onCheckedChange={setAccessible}
                    data-testid="rsvp-accessible"
                    className="data-[state=checked]:bg-[#1A362D] data-[state=unchecked]:bg-[#D6D3D1]"
                  />
                </div>
              </div>
              <div className="flex items-start justify-between gap-3 rounded-xl border border-[#1C1917]/10 bg-white/80 px-4 py-3">
                <div className="flex items-start gap-2 text-sm">
                  <Car className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p>Transportation required</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#78716C]" data-testid="rsvp-transport-note">
                      Transfers will be available from Jaipur airport, railway station, and bus stations.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={transport}
                  onCheckedChange={setTransport}
                  data-testid="rsvp-transport"
                  className="data-[state=checked]:bg-[#1A362D] data-[state=unchecked]:bg-[#D6D3D1]"
                />
              </div>
              <div className="space-y-3 rounded-xl border border-[#1C1917]/10 bg-white/80 px-4 py-4" data-testid="rsvp-travel">
                <div>
                  <Label>How are you arriving?</Label>
                  <p className="mt-1 text-xs text-[#78716C]">Share your arrival and the date and time you will need a drop-off. We will confirm the car on this page.</p>
                </div>
                <Select value={arrivalMode || undefined} onValueChange={setArrivalMode}>
                  <SelectTrigger data-testid="rsvp-arrival-mode"><SelectValue placeholder="Car, air, train, or bus" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Arriving by car</SelectItem>
                    <SelectItem value="air">Arriving by air</SelectItem>
                    <SelectItem value="train">Arriving by train</SelectItem>
                    <SelectItem value="bus">Arriving by bus</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder={arrivalMode === "air" ? "Flight number and airport" : arrivalMode === "train" ? "Train and station" : "Details (optional)"}
                  value={arrivalDetails}
                  onChange={(e) => setArrivalDetails(e.target.value)}
                  data-testid="rsvp-arrival-details"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Arrival date</Label>
                    <Input type="date" value={arrivalDate} onChange={(e) => setArrivalDate(e.target.value)} data-testid="rsvp-arrival-date" />
                  </div>
                  <div className="space-y-1">
                    <Label>Arrival time</Label>
                    <Input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} data-testid="rsvp-arrival-time" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Drop-off date</Label>
                    <Input type="date" value={departureDate} onChange={(e) => setDepartureDate(e.target.value)} data-testid="rsvp-departure-date" />
                  </div>
                  <div className="space-y-1">
                    <Label>Drop-off time</Label>
                    <Input type="time" value={departureTime} onChange={(e) => setDepartureTime(e.target.value)} data-testid="rsvp-departure-time" />
                  </div>
                </div>
                <Input
                  placeholder="Where should we drop you?"
                  value={dropoff}
                  onChange={(e) => setDropoff(e.target.value)}
                  data-testid="rsvp-dropoff"
                />
              </div>
              <div className="space-y-2">
                <Label>Meal Preference</Label>
                <Select value={meal} onValueChange={setMeal}>
                  <SelectTrigger data-testid="rsvp-meal"><SelectValue placeholder="Select preference" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vegetarian">Vegetarian</SelectItem>
                    <SelectItem value="Non-Vegetarian">Non-Vegetarian</SelectItem>
                    <SelectItem value="No Preference">No Preference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {activities.length > 0 && (
                <div className="space-y-3 rounded-xl border border-[#1C1917]/10 bg-white/80 px-4 py-3">
                  <div className="space-y-1">
                    <Label>Tours & activities of interest</Label>
                    <p className="text-xs leading-relaxed text-[#78716C]" data-testid="rsvp-activity-concierge-note">
                      Selecting an option is interest only, not a booking. We will connect you with our concierge to arrange it and confirm availability.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {activities.map((a) => (
                      <label key={a.id} className="flex cursor-pointer items-start gap-3 text-sm">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={activityIds.includes(a.id)}
                          onChange={() => toggleActivity(a.id)}
                          data-testid={`rsvp-activity-${a.id}`}
                        />
                        <span>
                          <span className="font-medium">{a.name}</span>
                          {a.timing_hint && <span className="block text-xs text-[#78716C]">{a.timing_hint}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                  <Input
                    placeholder="Notes for the concierge (group size, preferred dates, mobility needs…)"
                    value={activityNotes}
                    onChange={(e) => setActivityNotes(e.target.value)}
                    data-testid="rsvp-activity-notes"
                  />
                </div>
              )}
            </motion.div>
          )}

          {status !== "attending" && status !== "declined" && (
            <p className="mt-5 text-center text-xs text-[#78716C]">
              Choose Joyfully Accept to enter guests and tours you would like the concierge to book.
            </p>
          )}

          {detailsEditable && (status === "attending" || !attendanceLocked) && (
          <div className="mt-5 space-y-2">
            <Label>Special request</Label>
            <Textarea
              value={specialRequest}
              onChange={(e) => setSpecialRequest(e.target.value)}
              placeholder="Anything we can help with — a quiet room, a later pickup, dietary needs beyond the meal choice…"
              data-testid="rsvp-special-request"
            />
          </div>
          )}

          {wishesEditable && (
          <div className="mt-5 space-y-2">
            <Label>A Note to the Couple</Label>
            <RichTextEditor
              key={`rsvp-comments-${guest.id || "guest"}`}
              value={comments}
              onChange={setComments}
              placeholder="Share your wishes..."
              minHeight="100px"
              data-testid="rsvp-comments"
            />
          </div>
          )}

          <Button
            onClick={submit}
            disabled={saving}
            data-testid="rsvp-submit"
            className="mt-6 w-full py-6 text-base"
            style={{ background: accent }}
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : attendanceLocked ? "Update" : "Send RSVP"}
          </Button>
        </div>
      )}
    </Section>
  );
}

